import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { CLOUD_FUNCTIONS_URL } from '../firebase'
import { stripeConnectService } from '../services/StripeConnectService'

const planOptions = {
  basic: {
    label: 'Starter Plan',
    description: 'Best for new founders and small teams',
    amount: 4700
  },
  pro: {
    label: 'Growth Plan',
    description: 'Advanced automation and campaign growth tools',
    amount: 9700
  },
  enterprise: {
    label: 'Scale Plan',
    description: 'High-volume support and premium onboarding',
    amount: 19700
  }
}

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100)
}

const PartnerSignup = () => {
  const { partnerCode } = useParams()
  const [searchParams] = useSearchParams()
  const [partner, setPartner] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [selectedPlan, setSelectedPlan] = useState('basic')
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [paymentComplete, setPaymentComplete] = useState(false)
  const [paymentCancelled, setPaymentCancelled] = useState(false)

  const partnerBranding = partner?.brandingConfig || {}
  const brandLogo = partnerBranding.logoUrl || partner?.logoUrl
  const brandColor = partnerBranding.primaryColor || '#10b981'
  const brandDarkColor = partnerBranding.primaryColor ? partnerBranding.primaryColor : '#059669'
  const brandHeadline = partnerBranding.heroHeadline || `Buy through ${partner?.companyName || 'your partner'}`
  const brandSubheadline = partnerBranding.heroSubheadline || 'Complete your purchase with partner-specific pricing and support. Once payment is successful, continue with your account signup.'
  const partnerCompanyName = partner?.companyName || 'Your Partner'

  const heroAccentStyle = {
    background: `${brandColor}20`,
    borderColor: `${brandColor}40`
  }

  const primaryButtonStyle = {
    backgroundColor: brandColor,
    borderColor: brandColor
  }

  useEffect(() => {
    const planFromParams = searchParams.get('plan')
    const discountFromParams = parseInt(searchParams.get('discount'), 10)

    if (planFromParams && planOptions[planFromParams]) {
      setSelectedPlan(planFromParams)
    }

    if (searchParams.get('payment_success') === 'true') {
      setPaymentComplete(true)
    }

    if (searchParams.get('payment_cancelled') === 'true') {
      setPaymentCancelled(true)
    }
  }, [searchParams])

  useEffect(() => {
    const fetchPartner = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await fetch(`${CLOUD_FUNCTIONS_URL}/getPartnerByCode?partnerCode=${encodeURIComponent(partnerCode)}`)

        if (!response.ok) {
          const payload = await response.json().catch(() => null)
          console.error('Partner lookup failed:', response.status, payload)
          setError('Partner not found. Please verify the link or contact the partner directly.')
          setPartner(null)
          return
        }

        const data = await response.json()

        if (!data.success || !data.partner) {
          setError(data.error || 'Unable to load partner details. Please refresh or try again later.')
          setPartner(null)
          return
        }

        setPartner({ id: data.partner.id, ...data.partner.data })
      } catch (fetchError) {
        console.error('Error fetching partner data:', fetchError)
        setError('Unable to load partner details. Please refresh or try again later.')
      } finally {
        setIsLoading(false)
      }
    }

    if (partnerCode) {
      fetchPartner()
    } else {
      setError('Invalid partner link.')
      setIsLoading(false)
    }
  }, [partnerCode])

  const currentPlan = planOptions[selectedPlan]
  const rawDiscount = parseInt(searchParams.get('discount') || '0', 10)
  const discountValue = Number.isNaN(rawDiscount) ? 0 : Math.max(0, rawDiscount)
  const discountedAmount = Math.max(0, currentPlan.amount - Math.round((currentPlan.amount * discountValue) / 100))

  const handleCheckout = async () => {
    if (!email) {
      setError('Please enter your email address to continue.')
      return
    }

    if (!partner?.tenantId) {
      setError('This partner is not yet configured for payments. Please contact the partner directly.')
      return
    }

    if (!partner?.stripeAccountId) {
      setError('This partner has not completed Stripe onboarding yet. Please wait for the partner to connect their Stripe account.')
      return
    }

    setCheckoutLoading(true)
    setError(null)

    try {
      const successUrl = `${window.location.origin}/partner/${partnerCode}?payment_success=true&session_id={CHECKOUT_SESSION_ID}`
      const cancelUrl = `${window.location.origin}/partner/${partnerCode}?payment_cancelled=true`

      const response = await stripeConnectService.createPartnerCheckoutSession({
        partnerTenantId: partner.tenantId,
        customerEmail: email,
        lineItems: [
          {
            price_data: {
              currency: 'usd',
              product_data: {
                name: `${partner.companyName || 'White Label Partner'} - ${currentPlan.label}`
              },
              unit_amount: discountedAmount
            },
            quantity: 1
          }
        ],
        successUrl,
        cancelUrl,
        metadata: {
          partnerCode,
          plan: selectedPlan,
          partnerName: partner.companyName,
          customerName: name
        }
      })

      if (response.url) {
        window.location.href = response.url
      } else {
        setError('Unable to initiate payment. Please try again.')
      }
    } catch (checkoutError) {
      console.error('Partner checkout error:', checkoutError)
      setError(checkoutError.message || 'Unable to create checkout session.')
    } finally {
      setCheckoutLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-light)]">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-genie-teal mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading partner landing page...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-light)] px-6">
        <div className="max-w-xl rounded-3xl border border-gray-200 bg-white p-10 shadow-lg">
          <h1 className="text-3xl font-semibold text-gray-900">Partner Link Error</h1>
          <p className="mt-4 text-gray-600">{error}</p>
          <p className="mt-6 text-sm text-gray-500">If you arrived here from a partner referral, ask them to resend the link or verify their onboarding.</p>
          <Link to="/" className="mt-6 inline-flex rounded-full bg-genie-teal px-6 py-3 text-sm font-semibold text-white hover:bg-genie-teal-dark">Return to Market Genie</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--bg-light)] py-10 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl rounded-3xl border border-gray-200 bg-white p-8 shadow-lg">
        <div className="mb-8 flex flex-col gap-4 rounded-3xl border p-6" style={heroAccentStyle}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.24em] font-semibold" style={{ color: brandDarkColor }}>Partner referral landing page</p>
              <h1 className="text-4xl font-semibold text-gray-900">{brandHeadline}</h1>
              <p className="max-w-2xl text-gray-600">{brandSubheadline}</p>
            </div>
            {brandLogo ? (
              <img src={brandLogo} alt={`${partnerCompanyName} logo`} className="h-16 w-auto rounded-xl border border-gray-200 bg-white p-2" />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-gray-100 flex items-center justify-center text-xl font-semibold text-gray-500">{partnerCompanyName.charAt(0)}</div>
            )}
          </div>
          <p className="text-sm text-gray-500">Partner code: <strong>{partnerCode}</strong></p>
        </div>

        {paymentComplete ? (
          <div className="rounded-3xl border border-green-200 bg-green-50 p-6">
            <h2 className="text-2xl font-semibold text-green-900">Payment successful!</h2>
            <p className="mt-3 text-gray-700">Your purchase has been completed. Click below to finish setting up your account.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={`/signup?partner=${partnerCode}`} className="rounded-full bg-genie-teal px-6 py-3 text-sm font-semibold text-white hover:bg-genie-teal-dark">Continue to Signup</Link>
              <Link to="/" className="rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50">Return to Home</Link>
            </div>
          </div>
        ) : paymentCancelled ? (
          <div className="rounded-3xl border border-yellow-200 bg-yellow-50 p-6">
            <h2 className="text-2xl font-semibold text-yellow-900">Payment cancelled</h2>
            <p className="mt-3 text-gray-700">You can try again at any time or contact the partner for help.</p>
          </div>
        ) : null}

        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <section className="space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Selected plan</h2>
                  <p className="mt-2 text-sm text-gray-500">Partner-aligned pricing powered by Stripe Connect.</p>
                </div>
                <span className="rounded-full bg-genie-teal/10 px-3 py-1 text-sm font-semibold text-genie-teal">{currentPlan.label}</span>
              </div>
              <div className="mt-6 space-y-4">
                <div className="rounded-3xl bg-gray-50 p-5">
                  <p className="text-sm text-gray-500">Plan details</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">{currentPlan.description}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-3xl border border-gray-200 bg-white p-5">
                    <p className="text-sm text-gray-500">Base price</p>
                    <p className="mt-2 text-2xl font-semibold text-gray-900">{formatCurrency(currentPlan.amount)}</p>
                  </div>
                  <div className="rounded-3xl border border-gray-200 bg-white p-5">
                    <p className="text-sm text-gray-500">Final total</p>
                    <p className="mt-2 text-2xl font-semibold text-gray-900">{formatCurrency(discountedAmount)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900">Complete purchase</h2>
              <p className="mt-2 text-sm text-gray-500">Enter your details and complete checkout with Stripe.</p>
              <div className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Full name</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Your name"
                    className="mt-2 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none focus:border-genie-teal focus:ring-2 focus:ring-genie-teal/10"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Email address</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none focus:border-genie-teal focus:ring-2 focus:ring-genie-teal/10"
                  />
                </label>
                {partner?.stripeAccountId ? (
                  <button
                    type="button"
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                    className="inline-flex w-full items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"
                    style={primaryButtonStyle}
                  >
                    {checkoutLoading ? 'Starting checkout…' : `Pay ${formatCurrency(discountedAmount)}`}
                  </button>
                ) : (
                  <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
                    Partner onboarding is still in progress. Checkout is unavailable until the partner completes Stripe Connect onboarding.
                  </div>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
              <h3 className="text-lg font-semibold text-gray-900">Why partner checkout?</h3>
              <ul className="mt-4 space-y-3 text-gray-700">
                <li>• Partner receives revenue share automatically via Stripe Connect</li>
                <li>• Your purchase is attributed to the correct partner code</li>
                <li>• Supports secure Stripe-managed checkout and payouts</li>
              </ul>
            </div>
            <div className="rounded-3xl border border-gray-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-gray-900">Next steps after payment</h3>
              <p className="mt-3 text-gray-600">After payment, use the continue button to create your Market Genie account and verify your email.</p>
            </div>
            <div className="rounded-3xl border border-gray-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-gray-900">Partner details</h3>
              <p className="mt-3 text-sm text-gray-500">Partner company</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{partner.companyName || 'White Label Partner'}</p>
              {partner.stripeAccountId ? (
                <p className="mt-3 text-sm text-green-700">Stripe onboarding completed ✅</p>
              ) : (
                <p className="mt-3 text-sm text-yellow-700">Stripe onboarding pending</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default PartnerSignup
